import ReactGA from "react-ga";
import { FunctionComponent, useContext, useEffect } from "react";
import { Route, Switch, NavLinkProps, Redirect } from "react-router-dom";
import { Shell } from "./screens/Screen";
import { CompanyList } from "./screens/Company/CompanyList";
import { CreateCompany } from "./screens/Company/Company";
import { CompanyAdminHomePage } from "./screens/CompanyAdmin/HomePage";
import { UserContext } from "./components/User/UserProvider";
import { EditUser } from "./screens/User";
import { CompanyUsers } from "./screens/Company/CompanyUsers";
import { CompanyUserGroups } from "./screens/Company/CompanyUserGroups";
import { CreateUserGroup, EditUserGroup } from "./screens/Company/UserGroup";
import { CreateClient, EditClient } from "./screens/Client/Client";
import { ClientList } from "./screens/Client/ClientList";
import { EditGlobalAdmins } from "./screens/GlobalAdmins";
import { config } from "./config";
import { ResetOnboarding } from "./screens/ResetOnboarding";
import { LicensePackageList } from "./screens/License/LicensePackageList";
import { CreateLicensePackage, EditLicensePackage } from "./screens/License/LicensePackage";
import { FeatureList } from "./screens/Feature/FeatureList";
import { CreateProduct, EditProduct } from "./screens/Product/Product";
import { ProductList } from "./screens/Product/ProductList";
import { CreateLicense, EditLicense } from "./screens/License/License";
import { LicensesOverview } from "./screens/License/LicensesOverview";
import { ProductLicenseDetails } from "./screens/Product/ProductLicenseDetails";
import { EditCompanyAccessOption } from "./screens/Company/CompanyAccessOption";
import { DomainRequestList } from "./screens/Company/DomainRequestList";
import { SdksOverview } from "./screens/SDK/SdksOverview";
import { AnalyzeDeploymentType, Role } from "./graphql/types";
import { PrinterGroupsList } from "./screens/Printer/PrinterGroupsList";
import { CompanyPrinters } from "./screens/Printer/CompanyPrinters";
import { PrinterGroupList } from "./screens/Printer/PrinterGroup";
import { ProtectAMUpdates } from "./screens/ProtectAM/ProtectAMUpdates";
import { StaleCompanies } from "./screens/Company/StaleCompanies";
import { DisableOnboarding } from "./screens/DisableOnboarding";
import {
    CreateCompanyFromWorkbench,
    UpdateCompanyFromWorkbench,
} from "./screens/Company/WorkbenchCompany";
import { Streams } from "./screens/Company/Streams";
import urlJoin from "url-join";
import { ANALYZE_ROUTE } from "./utils/constants";
import { DisableTOS } from "./screens/DisableTOS";
import { ConfigureAnalyze } from "./screens/ConfigureAnalyze/ConfigureAnalyze";
import { ConfigureAnalyzeOnPrem } from "./screens/ConfigureAnalyze/ConfigureAnalyzeOnPrem";
import { ConfigureAnalyzeSaaS } from "./screens/ConfigureAnalyze/ConfigureAnalyzeSaas";
import { DebugTools } from "./screens/Debug/DebugTools";
import { VersionManagement } from "./screens/VersionManagement/VersionManagement";
import { EditCompany } from "./screens/Company/EditCompany";
import Devices from "./screens/Devices/Devices";

export const PATHS = {
    user: "/user",
    users: "/users",
    userGroup: "/user_group",
    userGroups: "/user_groups",
    newUserGroup: "/new_user_group",
    company: "/company",
    editCompany: "/edit_company",
    client: "/client",
    globalAdmins: "/admins",
    adminHome: "/",
    resetOnboarding: "/obscure_tools/reset_onboarding",
    staleCompanies: "/obscure_tools/stale_companies",
    disableOnboarding: "/obscure_tools/disable_onboarding",
    disableTOS: "/obscure_tools/disable_tos",
    licensePackage: "/license_package",
    licenses: "/licenses",
    feature: "/feature",
    product: "/product",
    license: "/license",
    domainRequests: "/domain_requests",
    sdk: "/sdk",
    printerGroups: "/printer_groups",
    companyPrinters: "/company_printers",
    companyDevices: "/company_devices",
    printerGroup: "/printer_group",
    protectAMUpdates: "/protectam_updates",
    streams: "/streams",
    versionManagment: "/version_management",
    configureAnalyze: "/configure_analyze",
    configureAnalyzeOnPrem: "/configure_analyze_on_prem",
    configureAnalyzeSaaS: "/configure_analyze_saas",
    debugTools: "/debug_tools",
};

export function editAccessOptionPath(optionId: number) {
    return `/company/access_option/${optionId}`;
}

const RedirectToHome: FunctionComponent = () => <Redirect to="/" />;

// FIXME GC-81780: Candidate for consolidating between server + frontend
export function fullAnalyzeUrl(
    baseUrl: string,
    deploymentType: AnalyzeDeploymentType = "saas"
): string {
    // We have to join with an empty string here because `urlJoin` treats an IPv6 hostname as if it
    // contains a malformed protocol string and tries to fix the protocol if it's the first argument:
    // https://github.com/jfromaniello/url-join/blob/v5.0.0/lib/url-join.js#L19
    const analyzeUrl = new URL(ANALYZE_ROUTE, baseUrl);
    return urlJoin(
        config.REACT_APP_LOGIN_URL,
        `?return_to=${analyzeUrl}`,
        deploymentType === "onPrem" ? "&expose_auth" : ""
    );
}

interface IRoutesProps {
    location: NavLinkProps["location"];
    refreshTheme: () => void;
}

export const Routes: FunctionComponent<IRoutesProps> = (props) => {
    const { location, refreshTheme } = props;
    const { role, companyId } = useContext(UserContext);

    // Google analytics page view instrumentation
    useEffect(() => {
        ReactGA.set({ page: window.location.pathname });
        ReactGA.pageview(window.location.pathname);
        refreshTheme();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    if (role !== Role.GlobalAdmin && !companyId) {
        window.location.assign(`${config.REACT_APP_PROFILE_URL}`);
        return null;
    }
    switch (role) {
        case Role.GlobalAdmin:
            return (
                <Switch location={location}>
                    <Route path={PATHS.globalAdmins} component={Shell(EditGlobalAdmins)} />
                    <Route path={`${PATHS.company}/new`} component={Shell(CreateCompany)} />
                    <Route
                        path={`${PATHS.company}/wb`}
                        component={Shell(CreateCompanyFromWorkbench)}
                        exact
                    />
                    <Route
                        path={`${PATHS.company}/wb/:companyId`}
                        component={Shell(UpdateCompanyFromWorkbench)}
                    />
                    <Route
                        exact
                        path={`${PATHS.company}/:companyId`}
                        component={Shell(EditCompany)}
                    />
                    <Route
                        path={`${PATHS.license}/assign/:companyId`}
                        component={Shell(CreateLicense)}
                    />
                    <Route path={`${PATHS.license}/:id`} component={Shell(EditLicense)} />
                    <Route path={`${PATHS.client}/new`} component={Shell(CreateClient)} />
                    <Route path={`${PATHS.client}/:id`} component={Shell(EditClient)} />
                    <Route path={PATHS.client} component={Shell(ClientList)} />
                    <Route
                        path={`${PATHS.licensePackage}/new`}
                        component={Shell(CreateLicensePackage)}
                    />
                    <Route
                        path={`${PATHS.licensePackage}/:id`}
                        component={Shell(EditLicensePackage)}
                    />
                    <Route path={PATHS.licensePackage} component={Shell(LicensePackageList)} />
                    <Route path={PATHS.feature} component={Shell(FeatureList)} />
                    <Route path={`${PATHS.product}/new`} component={Shell(CreateProduct)} />
                    <Route path={`${PATHS.product}/:id`} component={Shell(EditProduct)} />
                    <Route path={PATHS.product} component={Shell(ProductList)} />
                    <Route path={PATHS.domainRequests} component={Shell(DomainRequestList)} />
                    <Route path={PATHS.streams} component={Shell(Streams)} />
                    <Route
                        path={`${PATHS.company}/:companyId/access_option/:id`}
                        component={Shell(EditCompanyAccessOption)}
                    />
                    <Route exact path="/" component={Shell(CompanyList)} />
                    <Route path={PATHS.staleCompanies} component={Shell(StaleCompanies)} />
                    <Route path={PATHS.debugTools} component={Shell(DebugTools)} />
                    <Redirect to="/" />;
                </Switch>
            );
        case Role.CompanyAdmin:
        case Role.User:
            return (
                <Switch location={location}>
                    <Route path={PATHS.newUserGroup} component={Shell(CreateUserGroup)} />
                    <Route path={PATHS.userGroups} component={Shell(CompanyUserGroups)} />
                    <Route path={PATHS.users} component={Shell(CompanyUsers)} />
                    <Route path={`${PATHS.userGroup}/:id`} component={Shell(EditUserGroup)} />
                    <Route path={`${PATHS.user}/:email`} component={Shell(EditUser)} />
                    <Route path={`${PATHS.editCompany}`} component={Shell(EditCompany)} />
                    <Route
                        path={`${PATHS.company}/access_option/:id`}
                        component={
                            role === Role.CompanyAdmin
                                ? Shell(EditCompanyAccessOption)
                                : RedirectToHome
                        }
                    />
                    <Route path={`${PATHS.licenses}`} component={Shell(LicensesOverview)} />
                    <Route path={PATHS.versionManagment} component={Shell(VersionManagement)} />
                    <Route
                        path={`${PATHS.product}/:id`}
                        component={
                            role === Role.CompanyAdmin || role === Role.User
                                ? Shell(ProductLicenseDetails)
                                : RedirectToHome
                        }
                    />
                    <Route path={`${PATHS.sdk}`} component={Shell(SdksOverview)} />
                    <Route
                        path={PATHS.printerGroups}
                        component={
                            role === Role.CompanyAdmin ? Shell(PrinterGroupsList) : RedirectToHome
                        }
                    />
                    <Route
                        path={PATHS.companyPrinters}
                        component={
                            role === Role.CompanyAdmin ? Shell(CompanyPrinters) : RedirectToHome
                        }
                    />
                    <Route
                        path={PATHS.companyDevices}
                        component={role === Role.CompanyAdmin ? Shell(Devices) : RedirectToHome}
                    />
                    <Route
                        path={`${PATHS.printerGroup}/:id`}
                        component={
                            role === Role.CompanyAdmin ? Shell(PrinterGroupList) : RedirectToHome
                        }
                    />
                    <Route exact path={PATHS.resetOnboarding} component={ResetOnboarding} />
                    <Route exact path={PATHS.disableOnboarding} component={DisableOnboarding} />
                    <Route exact path={PATHS.disableTOS} component={DisableTOS} />
                    <Route path={PATHS.protectAMUpdates} component={Shell(ProtectAMUpdates)} />
                    <Route path={PATHS.configureAnalyze} component={Shell(ConfigureAnalyze)} />
                    <Route
                        path={PATHS.configureAnalyzeOnPrem}
                        component={Shell(ConfigureAnalyzeOnPrem)}
                    />
                    <Route
                        path={PATHS.configureAnalyzeSaaS}
                        component={Shell(ConfigureAnalyzeSaaS)}
                    />
                    <Route
                        exact
                        path="/"
                        component={Shell(CompanyAdminHomePage, { offWhiteBG: true })}
                    />
                    <Redirect to="/" />;
                </Switch>
            );
        default:
            // Fallback — should not be reached due to the companyId guard above.
            window.location.assign(
                `${config.REACT_APP_PROFILE_URL || "https://login.grabcad.com/profile"}`
            );
            return null;
    }
};
